import axios from "axios";

let IGP_BASE_URL = process.env.REACT_APP_IGP_BASE_URL;
if (IGP_BASE_URL) IGP_BASE_URL = IGP_BASE_URL.replace(/\/$/, "");

let IGP_CONTENT_SERVER_BASE_URL =
  process.env.REACT_APP_IGP_CONTENT_SERVER_VALIDATE_URL;
if (IGP_CONTENT_SERVER_BASE_URL)
  IGP_CONTENT_SERVER_BASE_URL = IGP_CONTENT_SERVER_BASE_URL.replace(/\/$/, "");

const IGP_CONTENT_SERVER_JWE_SECRET =
  process.env.REACT_APP_IGP_CONTENT_SERVER_ENCR_SECRET;
const IGP_CONTENT_SERVER_DEVICE = {
  device_id: "content-server",
  platform: "server",
  browser: "n/a",
  version: "1.0",
  os: "n/a",
};

const DEFAULT_CAT_TTL = 86400;

const base64UrlToUint8Array = (value) => {
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized.padEnd(
    normalized.length + ((4 - (normalized.length % 4)) % 4),
    "=",
  );
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
};

const utf8ToUint8Array = (value) => new TextEncoder().encode(value);

const deriveResponseKey = async (secret) => {
  const secretBytes = utf8ToUint8Array(secret);
  const hash = await crypto.subtle.digest("SHA-256", secretBytes);
  return crypto.subtle.importKey("raw", hash, { name: "AES-GCM" }, false, [
    "decrypt",
  ]);
};

const decryptResponse = async (token, secret) => {
  const parts = token.split(".");
  if (parts.length !== 5) {
    throw new Error("Invalid JWE token format from content server");
  }

  const [protectedHeader, , ivPart, ciphertextPart, tagPart] = parts;
  const key = await deriveResponseKey(secret);
  const iv = base64UrlToUint8Array(ivPart);
  const ciphertext = base64UrlToUint8Array(ciphertextPart);
  const tag = base64UrlToUint8Array(tagPart);
  const encryptedData = new Uint8Array(ciphertext.length + tag.length);
  encryptedData.set(ciphertext, 0);
  encryptedData.set(tag, ciphertext.length);

  const decrypted = await crypto.subtle.decrypt(
    {
      name: "AES-GCM",
      iv,
      additionalData: utf8ToUint8Array(protectedHeader),
      tagLength: 128,
    },
    key,
    encryptedData,
  );

  const plaintext = new TextDecoder().decode(decrypted);
  try {
    return JSON.parse(plaintext);
  } catch (error) {
    return plaintext;
  }
};

const unwrapResponse = async (responseData) => {
  if (!responseData) {
    return responseData;
  }

  if (typeof responseData === "string") {
    if (!responseData.includes(".")) {
      return responseData;
    }
    if (!IGP_CONTENT_SERVER_JWE_SECRET) {
      throw new Error("Missing content-server JWE secret");
    }
    return decryptResponse(responseData, IGP_CONTENT_SERVER_JWE_SECRET);
  }

  if (typeof responseData !== "object") {
    return responseData;
  }

  const token =
    responseData.key ||
    responseData.token ||
    responseData.jwe ||
    responseData.encrypted_response;

  if (!token || typeof token !== "string") {
    return responseData;
  }

  if (!token.includes(".")) {
    return responseData;
  }

  if (!IGP_CONTENT_SERVER_JWE_SECRET) {
    throw new Error("Missing content-server JWE secret");
  }

  return decryptResponse(token, IGP_CONTENT_SERVER_JWE_SECRET);
};

/**
 * Set IGP base URL at runtime.
 * Expects a validated base_url from the API payload.
 */
export const setIgpBaseUrl = (url) => {
  const newBaseUrl =
    typeof url === "string" ? url.trim().replace(/\/$/, "") : "";
  if (!newBaseUrl || newBaseUrl === IGP_BASE_URL) return;

  IGP_BASE_URL = newBaseUrl;
};

/**
 * Validates a content-server token (ctoken) and returns the raw validation payload.
 * @param {string} ctoken - The content server token to validate.
 * @param {number} [catTtl=DEFAULT_CAT_TTL] - CAT token TTL in seconds.
 * @returns {Promise<Object>} The raw validation response data.
 */
export const validateContentServerToken = async (
  ctoken,
  catTtl = DEFAULT_CAT_TTL,
) => {
  if (!ctoken || typeof ctoken !== "string") {
    throw new Error(
      "validateContentServerToken requires a valid ctoken string",
    );
  }

  try {
    console.log("Validating started");
    const response = await axios.post(
      `${IGP_CONTENT_SERVER_BASE_URL}/validate`,
      {
        ctoken,
        device: IGP_CONTENT_SERVER_DEVICE,
        cat_ttl: catTtl,
      },
    );

    return unwrapResponse(response.data);
  } catch (error) {
    console.error("Error validating content server token:", error);
    throw error;
  }
};

/**
 * Normalizes grant ID for access API calls. The backend may send grant IDs with
 * a chunk suffix (e.g. grant_xxx:1). The access API expects the base ID.
 * @param {string} grantId - Raw grant ID from session
 * @returns {string} Grant ID with trailing :digits stripped
 */
export const normalizeGrantIdForAccess = (grantId) => {
  if (!grantId || typeof grantId !== "string") return grantId;
  return grantId.replace(/:\d+$/, "") || grantId;
};

/**
 * Gets tokens from sessionStorage (single source of truth).
 * Returns grantId normalized for access API (strips trailing :chunk suffix).
 * Used by all API callers (desktop and mobile); no separate mobile path.
 * @returns {Object} { grantId, agtToken, rstToken, catToken, baseUrl, documentId } from session
 * @throws {Error} If session is not found or grantId is missing
 */
export const getSessionTokens = () => {
  const savedSession = sessionStorage.getItem("igp_session");
  if (!savedSession) throw new Error("No session found");
  const {
    grantId: rawGrantId,
    agtToken,
    rstToken,
    catToken,
    baseUrl,
    documentId,
  } = JSON.parse(savedSession);
  if (!rawGrantId) throw new Error("Missing grantId in session");
  const grantId = normalizeGrantIdForAccess(rawGrantId);
  return { grantId, agtToken, rstToken, catToken, baseUrl, documentId };
};

/**
 * Returns a valid RST token, using sessionStorage as single source of truth.
 * @param {string} grantId - The grant ID (required)
 * @param {string} [agtToken] - Optional AGT token. Not used for session-based RST retrieval.
 * @returns {Promise<string>} - RST token string
 */
export const getValidRstToken = async (grantId, agtToken) => {
  if (!grantId) throw new Error("getValidRstToken requires a grantId");

  try {
    const savedSession = sessionStorage.getItem("igp_session");
    if (savedSession) {
      const session = JSON.parse(savedSession);
      if (session.rstToken) {
        return session.rstToken;
      }
    }
  } catch (e) {
    console.warn("Failed to read RST token from sessionStorage:", e.message);
  }

  throw new Error("No RST token found in sessionStorage");
};

/**
 * Fetches a pre-signed URL for a document from the IGP Content Server.
 * @param {string} catToken - The CAT token for authorization.
 * @param {string} documentId - The ID of the document.
 * @param {string} fileName - The name of the file to request (e.g., "metadata.json", "NL2.pdf", "pages/1-5.pdf").
 * @returns {Promise<Object>} The API response containing the pre-signed URL.
 */
export const getPresignedUrl = async (catToken, documentId, fileName) => {
  try {
    const body = {
      document_id: documentId,
      ttl: 3600,
    };
    if (fileName) body.file_name = fileName;
    const response = await axios.post(
      `${IGP_CONTENT_SERVER_BASE_URL}/read`,
      body,
      {
        headers: {
          Authorization: `Bearer ${catToken}`,
          "Content-Type": "application/json",
        },
      },
    );
    return unwrapResponse(response.data);
  } catch (error) {
    console.error(
      `Error fetching pre-signed URL for ${fileName || documentId}:`,
      error,
    );
    throw error;
  }
};

/**
 * Fetches JSON data from a pre-signed URL.
 * @param {string} url - The pre-signed URL.
 * @returns {Promise<Object>} The JSON data.
 */
export const fetchJsonFromUrl = async (url) => {
  try {
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error("Error fetching JSON from URL:", error);
    throw error;
  }
};

/**
 * Fetches binary (arraybuffer) data from a pre-signed URL. Useful for PDFs and EPUBs.
 * @param {string} url - The pre-signed URL.
 * @returns {Promise<ArrayBuffer>} The binary data.
 */
export const fetchArrayBufferFromUrl = async (url) => {
  try {
    const response = await axios.get(url, {
      responseType: "arraybuffer",
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching binary data from URL:", error);
    throw error;
  }
};

// ============================================
// ANNOTATION APIs (Bookmarks, Highlights, Notes, Comments)
// ============================================

/**
 * Fetches user annotations for a document (bookmarks, highlights, notes, comments, etc.)
 * @param {string} grantId - The grant ID.
 * @param {string} rstToken - The RST token.
 * @param {Array<string>} fields - Optional: specific fields to fetch (e.g., ['bookmarks', 'highlights']).
 *                                  If empty, fetches all fields.
 * @returns {Promise<Object>} The annotation data from the API.
 */
export const fetchAccessData = async (grantId, rstToken, fields = []) => {
  try {
    const query = fields.length ? `?fields=${fields.join(",")}` : "";
    const response = await axios.get(
      `${IGP_BASE_URL}/access/${grantId}${query}`,
      {
        headers: {
          Authorization: `Bearer ${rstToken}`,
          "Content-Type": "application/json",
        },
      },
    );
    return response.data.data;
  } catch (error) {
    if (error?.response?.status === 404) {
      return {};
    }
    console.error("Error fetching access data:", error);
    throw error;
  }
};

/**
 * Creates a new bookmark.
 * @param {string} grantId - The grant ID.
 * @param {string} rstToken - The RST token.
 * @param {Object} bookmarkData - { page_number, title (optional) }
 * @returns {Promise<Object>} The API response.
 */
export const createBookmark = async (grantId, rstToken, bookmarkData) => {
  try {
    const response = await axios.post(
      `${IGP_BASE_URL}/access/${grantId}`,
      {
        bookmarks: {
          data: [
            {
              id:
                bookmarkData.id ||
                `bmk_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              action: "create",
              page_number: bookmarkData.page_number,
              title: bookmarkData.title || "",
              timestamp: new Date().toISOString(),
            },
          ],
        },
      },
      {
        headers: {
          Authorization: `Bearer ${rstToken}`,
          "Content-Type": "application/json",
        },
      },
    );
    return response.data.data;
  } catch (error) {
    console.error("Error creating bookmark:", error);
    throw error;
  }
};

/**
 * Updates an existing bookmark.
 * @param {string} grantId - The grant ID.
 * @param {string} rstToken - The RST token.
 * @param {string} bookmarkId - The ID of the bookmark to update.
 * @param {Object} updateData - { page_number (optional), title (optional) }
 * @returns {Promise<Object>} The API response.
 */
export const updateBookmark = async (
  grantId,
  rstToken,
  bookmarkId,
  updateData,
) => {
  try {
    const response = await axios.post(
      `${IGP_BASE_URL}/access/${grantId}`,
      {
        bookmarks: {
          data: [
            {
              id: bookmarkId,
              action: "update",
              page_number: updateData.page_number,
              title: updateData.title || "",
              timestamp: new Date().toISOString(),
            },
          ],
        },
      },
      {
        headers: {
          Authorization: `Bearer ${rstToken}`,
          "Content-Type": "application/json",
        },
      },
    );
    return response.data.data;
  } catch (error) {
    console.error("Error updating bookmark:", error);
    throw error;
  }
};

/**
 * Deletes a bookmark.
 * @param {string} grantId - The grant ID.
 * @param {string} rstToken - The RST token.
 * @param {string} bookmarkId - The ID of the bookmark to delete.
 * @returns {Promise<Object>} The API response.
 */
export const deleteBookmark = async (grantId, rstToken, bookmarkId) => {
  try {
    const response = await axios.post(
      `${IGP_BASE_URL}/access/${grantId}`,
      {
        bookmarks: {
          data: [
            {
              id: bookmarkId,
              action: "delete",
            },
          ],
        },
      },
      {
        headers: {
          Authorization: `Bearer ${rstToken}`,
          "Content-Type": "application/json",
        },
      },
    );
    return response.data.data;
  } catch (error) {
    console.error("Error deleting bookmark:", error);
    throw error;
  }
};

/**
 * Creates a new highlight.
 * @param {string} grantId - The grant ID.
 * @param {string} rstToken - The RST token.
 * @param {Object} highlightData - { page_number, content, color (e.g., '#FFFF00'), coordinates (optional), chapter_number (optional) }
 * @returns {Promise<Object>} The API response.
 */
export const createHighlight = async (grantId, rstToken, highlightData) => {
  try {
    const payload = {
      id:
        highlightData.id ||
        `hl_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      action: "create",
      page_number: highlightData.page_number,
      content: highlightData.content || "",
      color: highlightData.color || "#FFFF00",
      timestamp: new Date().toISOString(),
    };

    if (highlightData.chapter_number !== undefined) {
      payload.chapter_number = highlightData.chapter_number;
    }

    // Add coordinates if provided
    if (highlightData.coordinates) {
      payload.coordinates = highlightData.coordinates;
    }

    const response = await axios.post(
      `${IGP_BASE_URL}/access/${grantId}`,
      {
        annotations: {
          highlights: {
            data: [payload],
          },
        },
      },
      {
        headers: {
          Authorization: `Bearer ${rstToken}`,
          "Content-Type": "application/json",
        },
      },
    );
    return response.data.data;
  } catch (error) {
    console.error("Error creating highlight:", error);
    throw error;
  }
};

/**
 * Updates an existing highlight.
 * @param {string} grantId - The grant ID.
 * @param {string} rstToken - The RST token.
 * @param {string} highlightId - The ID of the highlight to update.
 * @param {Object} updateData - { page_number (optional), content (optional), color (optional), coordinates (optional) }
 * @returns {Promise<Object>} The API response.
 */
export const updateHighlight = async (
  grantId,
  rstToken,
  highlightId,
  updateData,
) => {
  try {
    const payload = {
      id: highlightId,
      action: "update",
      timestamp: new Date().toISOString(),
    };

    if (updateData.page_number !== undefined) {
      payload.page_number = updateData.page_number;
    }
    if (updateData.content !== undefined) {
      payload.content = updateData.content;
    }
    if (updateData.color !== undefined) {
      payload.color = updateData.color;
    }
    if (updateData.coordinates !== undefined) {
      payload.coordinates = updateData.coordinates;
    }

    const response = await axios.post(
      `${IGP_BASE_URL}/access/${grantId}`,
      {
        annotations: {
          highlights: {
            data: [payload],
          },
        },
      },
      {
        headers: {
          Authorization: `Bearer ${rstToken}`,
          "Content-Type": "application/json",
        },
      },
    );
    return response.data.data;
  } catch (error) {
    console.error("Error updating highlight:", error);
    throw error;
  }
};

/**
 * Deletes a highlight.
 * @param {string} grantId - The grant ID.
 * @param {string} rstToken - The RST token.
 * @param {string} highlightId - The ID of the highlight to delete.
 * @returns {Promise<Object>} The API response.
 */
export const deleteHighlight = async (grantId, rstToken, highlightId) => {
  try {
    const response = await axios.post(
      `${IGP_BASE_URL}/access/${grantId}`,
      {
        annotations: {
          highlights: {
            data: [
              {
                id: highlightId,
                action: "delete",
              },
            ],
          },
        },
      },
      {
        headers: {
          Authorization: `Bearer ${rstToken}`,
          "Content-Type": "application/json",
        },
      },
    );
    return response.data.data;
  } catch (error) {
    console.error("Error deleting highlight:", error);
    throw error;
  }
};

/**
 * Creates a new note.
 * @param {string} grantId - The grant ID.
 * @param {string} rstToken - The RST token.
 * @param {Object} noteData - { page_number, content, selected_text (optional), coordinates (optional), chapter_number (optional) }
 * @returns {Promise<Object>} The API response.
 */
export const createNote = async (grantId, rstToken, noteData) => {
  try {
    const payload = {
      id: `note_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      action: "create",
      page_number: noteData.page_number,
      content: noteData.content || "",
      timestamp: new Date().toISOString(),
    };

    // Add selected_text if provided
    if (noteData.selected_text !== undefined) {
      payload.selected_text = noteData.selected_text;
    }

    if (noteData.chapter_number !== undefined) {
      payload.chapter_number = noteData.chapter_number;
    }

    if (noteData.coordinates !== undefined) {
      payload.coordinates = noteData.coordinates;
    }

    const response = await axios.post(
      `${IGP_BASE_URL}/access/${grantId}`,
      {
        annotations: {
          notes: {
            data: [payload],
          },
        },
      },
      {
        headers: {
          Authorization: `Bearer ${rstToken}`,
          "Content-Type": "application/json",
        },
      },
    );
    return response.data.data;
  } catch (error) {
    console.error("Error creating note:", error);
    throw error;
  }
};

/**
 * Updates an existing note.
 * @param {string} grantId - The grant ID.
 * @param {string} rstToken - The RST token.
 * @param {string} noteId - The ID of the note to update.
 * @param {Object} updateData - { page_number (optional), content (optional), selected_text (optional), coordinates (optional), chapter_number (optional) }
 * @returns {Promise<Object>} The API response.
 */
export const updateNote = async (grantId, rstToken, noteId, updateData) => {
  try {
    const payload = {
      id: noteId,
      action: "update",
      timestamp: new Date().toISOString(),
    };
    if (updateData.page_number !== undefined) {
      payload.page_number = updateData.page_number;
    }
    if (updateData.content !== undefined) {
      payload.content = updateData.content;
    }
    if (updateData.selected_text !== undefined) {
      payload.selected_text = updateData.selected_text;
    }
    if (updateData.chapter_number !== undefined) {
      payload.chapter_number = updateData.chapter_number;
    }
    if (updateData.coordinates !== undefined) {
      payload.coordinates = updateData.coordinates;
    }

    const response = await axios.post(
      `${IGP_BASE_URL}/access/${grantId}`,
      {
        annotations: {
          notes: {
            data: [payload],
          },
        },
      },
      {
        headers: {
          Authorization: `Bearer ${rstToken}`,
          "Content-Type": "application/json",
        },
      },
    );
    return response.data.data;
  } catch (error) {
    console.error("Error updating note:", error);
    throw error;
  }
};

/**
 * Deletes a note.
 * @param {string} grantId - The grant ID.
 * @param {string} rstToken - The RST token.
 * @param {string} noteId - The ID of the note to delete.
 * @returns {Promise<Object>} The API response.
 */
export const deleteNote = async (grantId, rstToken, noteId) => {
  try {
    const response = await axios.post(
      `${IGP_BASE_URL}/access/${grantId}`,
      {
        annotations: {
          notes: {
            data: [
              {
                id: noteId,
                action: "delete",
              },
            ],
          },
        },
      },
      {
        headers: {
          Authorization: `Bearer ${rstToken}`,
          "Content-Type": "application/json",
        },
      },
    );
    return response.data.data;
  } catch (error) {
    console.error("Error deleting note:", error);
    throw error;
  }
};

/**
 * Creates a new comment.
 * @param {string} grantId - The grant ID.
 * @param {string} rstToken - The RST token.
 * @param {Object} commentData - { page_number, content }
 * @returns {Promise<Object>} The API response.
 */
export const createComment = async (grantId, rstToken, commentData) => {
  try {
    const response = await axios.post(
      `${IGP_BASE_URL}/access/${grantId}`,
      {
        comments: {
          data: [
            {
              id: `cmt_${Date.now()}_${Math.random()
                .toString(36)
                .substr(2, 9)}`,
              action: "create",
              page_number: commentData.page_number,
              content: commentData.content || "",
              timestamp: new Date().toISOString(),
            },
          ],
        },
      },
      {
        headers: {
          Authorization: `Bearer ${rstToken}`,
          "Content-Type": "application/json",
        },
      },
    );
    return response.data.data;
  } catch (error) {
    console.error("Error creating comment:", error);
    throw error;
  }
};

/**
 * Updates an existing comment.
 * @param {string} grantId - The grant ID.
 * @param {string} rstToken - The RST token.
 * @param {string} commentId - The ID of the comment to update.
 * @param {Object} updateData - { page_number (optional), content (optional) }
 * @returns {Promise<Object>} The API response.
 */
export const updateComment = async (
  grantId,
  rstToken,
  commentId,
  updateData,
) => {
  try {
    const response = await axios.post(
      `${IGP_BASE_URL}/access/${grantId}`,
      {
        comments: {
          data: [
            {
              id: commentId,
              action: "update",
              page_number: updateData.page_number,
              content: updateData.content || "",
              timestamp: new Date().toISOString(),
            },
          ],
        },
      },
      {
        headers: {
          Authorization: `Bearer ${rstToken}`,
          "Content-Type": "application/json",
        },
      },
    );
    return response.data.data;
  } catch (error) {
    console.error("Error updating comment:", error);
    throw error;
  }
};

/**
 * Deletes a comment.
 * @param {string} grantId - The grant ID.
 * @param {string} rstToken - The RST token.
 * @param {string} commentId - The ID of the comment to delete.
 * @returns {Promise<Object>} The API response.
 */
export const deleteComment = async (grantId, rstToken, commentId) => {
  try {
    const response = await axios.post(
      `${IGP_BASE_URL}/access/${grantId}`,
      {
        comments: {
          data: [
            {
              id: commentId,
              action: "delete",
            },
          ],
        },
      },
      {
        headers: {
          Authorization: `Bearer ${rstToken}`,
          "Content-Type": "application/json",
        },
      },
    );
    return response.data.data;
  } catch (error) {
    console.error("Error deleting comment:", error);
    throw error;
  }
};

// ============================================
// UNDERLINE APIs
// ============================================

/**
 * Creates a new underline annotation.
 * @param {string} grantId - The grant ID.
 * @param {string} rstToken - The RST token.
 * @param {Object} underlineData - { page_number, content, coordinates, chapter_number (optional) }
 * @returns {Promise<Object>} The API response.
 */
export const createUnderlineApi = async (grantId, rstToken, underlineData) => {
  try {
    const payload = {
      id:
        underlineData.id ||
        `ul_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      action: "create",
      page_number: underlineData.page_number,
      content: underlineData.content || "",
      timestamp: new Date().toISOString(),
    };

    if (underlineData.chapter_number !== undefined) {
      payload.chapter_number = underlineData.chapter_number;
    }

    // Add coordinates if provided
    if (underlineData.coordinates) {
      payload.coordinates = underlineData.coordinates;
    }

    const response = await axios.post(
      `${IGP_BASE_URL}/access/${grantId}`,
      {
        annotations: {
          underline: {
            data: [payload],
          },
        },
      },
      {
        headers: {
          Authorization: `Bearer ${rstToken}`,
          "Content-Type": "application/json",
        },
      },
    );
    return response.data.data;
  } catch (error) {
    console.error("Error creating underline:", error);
    throw error;
  }
};

/**
 * Deletes an underline annotation.
 * @param {string} grantId - The grant ID.
 * @param {string} rstToken - The RST token.
 * @param {string} underlineId - The ID of the underline to delete.
 * @returns {Promise<Object>} The API response.
 */
export const deleteUnderlineApi = async (grantId, rstToken, underlineId) => {
  try {
    const response = await axios.post(
      `${IGP_BASE_URL}/access/${grantId}`,
      {
        annotations: {
          underline: {
            data: [
              {
                id: underlineId,
                action: "delete",
              },
            ],
          },
        },
      },
      {
        headers: {
          Authorization: `Bearer ${rstToken}`,
          "Content-Type": "application/json",
        },
      },
    );
    return response.data.data;
  } catch (error) {
    // 404 or no response (e.g. CORS blocks error response) = treat as success, avoid log spam
    if (error?.response?.status === 404 || !error?.response) {
      return null;
    }
    console.error("Error deleting underline:", error);
    throw error;
  }
};

// ============================================
// TEXTBOX APIs
// ============================================

/**
 * Creates a new textbox annotation.
 * @param {string} grantId - The grant ID.
 * @param {string} rstToken - The RST token.
 * @param {Object} textboxData - { page_number, content, coordinates, ...dynamicFields }
 * @returns {Promise<Object>} The API response.
 */
export const createTextboxApi = async (grantId, rstToken, textboxData) => {
  try {
    const payload = {
      ...textboxData,
      id:
        textboxData.id ||
        `tb_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      action: "create",
      page_number: textboxData.page_number,
      content: textboxData.content || "",
      timestamp: new Date().toISOString(),
    };

    const response = await axios.post(
      `${IGP_BASE_URL}/access/${grantId}`,
      {
        annotations: {
          textbox: {
            data: [payload],
          },
        },
      },
      {
        headers: {
          Authorization: `Bearer ${rstToken}`,
          "Content-Type": "application/json",
        },
      },
    );
    return response.data.data;
  } catch (error) {
    console.error("Error creating textbox:", error);
    throw error;
  }
};

/**
 * Updates an existing textbox annotation.
 * @param {string} grantId - The grant ID.
 * @param {string} rstToken - The RST token.
 * @param {string} textboxId - The ID of the textbox to update.
 * @param {Object} updateData - Partial textbox fields
 * @returns {Promise<Object>} The API response.
 */
export const updateTextboxApi = async (
  grantId,
  rstToken,
  textboxId,
  updateData,
) => {
  try {
    const payload = {
      ...updateData,
      id: textboxId,
      action: "update",
      timestamp: new Date().toISOString(),
    };

    const response = await axios.post(
      `${IGP_BASE_URL}/access/${grantId}`,
      {
        annotations: {
          textbox: {
            data: [payload],
          },
        },
      },
      {
        headers: {
          Authorization: `Bearer ${rstToken}`,
          "Content-Type": "application/json",
        },
      },
    );
    return response.data.data;
  } catch (error) {
    console.error("Error updating textbox:", error);
    throw error;
  }
};

/**
 * Deletes a textbox annotation.
 * @param {string} grantId - The grant ID.
 * @param {string} rstToken - The RST token.
 * @param {string} textboxId - The ID of the textbox to delete.
 * @param {Object} textboxData - Optional textbox context fields (page_number, coordinates, etc.)
 * @returns {Promise<Object>} The API response.
 */
export const deleteTextboxApi = async (
  grantId,
  rstToken,
  textboxId,
  textboxData = {},
) => {
  try {
    const payload = {
      ...textboxData,
      id: textboxId,
      action: "delete",
      timestamp: new Date().toISOString(),
    };

    const response = await axios.post(
      `${IGP_BASE_URL}/access/${grantId}`,
      {
        annotations: {
          textbox: {
            data: [payload],
          },
        },
      },
      {
        headers: {
          Authorization: `Bearer ${rstToken}`,
          "Content-Type": "application/json",
        },
      },
    );
    return response.data.data;
  } catch (error) {
    console.error("Error deleting textbox:", error);
    throw error;
  }
};

// ============================================
// FREEHAND DRAWING APIs
// ============================================

/**
 * Creates a new freehand drawing annotation.
 * @param {string} grantId - The grant ID.
 * @param {string} rstToken - The RST token.
 * @param {Object} drawingData - { id, page_number, drawing_data, timestamp }
 * @returns {Promise<Object>} The API response.
 */
export const createFreehandDrawing = async (grantId, rstToken, drawingData) => {
  try {
    const response = await axios.post(
      `${IGP_BASE_URL}/access/${grantId}`,
      {
        annotations: {
          freehand_drawing: {
            data: [
              {
                id: drawingData.id,
                action: "create",
                page_number: drawingData.page_number,
                drawing_data: drawingData.drawing_data,
                timestamp: drawingData.timestamp,
              },
            ],
          },
        },
      },
      {
        headers: {
          Authorization: `Bearer ${rstToken}`,
          "Content-Type": "application/json",
        },
      },
    );
    return response.data.data;
  } catch (error) {
    console.error("Error creating freehand drawing:", error);
    throw error;
  }
};

/**
 * Deletes a freehand drawing annotation.
 * @param {string} grantId - The grant ID.
 * @param {string} rstToken - The RST token.
 * @param {string} drawingId - The ID of the drawing to delete.
 * @returns {Promise<Object>} The API response.
 */
export const deleteFreehandDrawing = async (grantId, rstToken, drawingId) => {
  try {
    const response = await axios.post(
      `${IGP_BASE_URL}/access/${grantId}`,
      {
        annotations: {
          freehand_drawing: {
            data: [
              {
                id: drawingId,
                action: "delete",
              },
            ],
          },
        },
      },
      {
        headers: {
          Authorization: `Bearer ${rstToken}`,
          "Content-Type": "application/json",
        },
      },
    );
    return response.data.data;
  } catch (error) {
    console.error("Error deleting freehand drawing:", error);
    throw error;
  }
};

// ============================================
// ANALYTICS LOGGING
// ============================================
/**
 * Send analytics log for reader events immediately.
 * @param {string} documentId
 * @param {string} action
 * @param {Object} data
 * @param {string} rstToken
 */
export const sendReaderLog = (documentId, action, data, rstToken) => {
  if (!documentId) return;
  if (!rstToken) {
    if (process.env.NODE_ENV !== "production") {
      console.debug("sendReaderLog skipped: missing rstToken");
    }
    return;
  }

  const baseUrl = IGP_BASE_URL;
  if (!baseUrl) return;

  const normalizedBase = baseUrl.replace(/\/$/, "");
  const url = /\/v1$/i.test(normalizedBase)
    ? `${normalizedBase}/log`
    : `${normalizedBase}/v1/log`;
  const body = {
    document_id: documentId,
    action: action || "reading",
    data: data || {},
  };

  return axios
    .post(url, body, {
      headers: {
        Authorization: `Bearer ${rstToken}`,
        "Content-Type": "application/json",
      },
    })
    .catch((err) => {
      if (process.env.NODE_ENV !== "production") {
        console.debug("Analytics log failed:", err.message);
      }
    });
};
